import shared from "../../../shared/gemini-voices.json";

export const GEMINI_VOICE_IDS = [
  "Zephyr", "Puck", "Charon", "Kore", "Fenrir", "Leda", "Orus", "Aoede",
  "Callirrhoe", "Autonoe", "Enceladus", "Iapetus", "Umbriel", "Algieba",
  "Despina", "Erinome", "Algenib", "Rasalgethi", "Laomedeia", "Achernar",
  "Alnilam", "Schedar", "Gacrux", "Pulcherrima", "Achird", "Zubenelgenubi",
  "Vindemiatrix", "Sadachbia", "Sadaltager", "Sulafat",
] as const;

export type GeminiVoiceId = (typeof GEMINI_VOICE_IDS)[number];

export const DEFAULT_GEMINI_VOICE: GeminiVoiceId = "Charon";

export const isGeminiVoiceId = (value: unknown): value is GeminiVoiceId =>
  typeof value === "string" && (GEMINI_VOICE_IDS as readonly string[]).includes(value);

// What the voice picker shows of each voice (owner, 2026-11-02): the repo-root shared/gemini-voices.json, Google's
// gender and one-word style for each of the 30 voices, which geminiVoices.test.ts holds GEMINI_VOICE_IDS to.
export const VOICE_GENDERS = ["female", "male"] as const;
export type VoiceGender = (typeof VOICE_GENDERS)[number];
export const VOICE_GENDER_LABELS: Record<VoiceGender, string> = { female: "Female", male: "Male" };

/** The picker's gender filter: every voice, or one gender. */
export type VoiceGenderFilter = "all" | VoiceGender;
export const VOICE_GENDER_FILTERS: readonly VoiceGenderFilter[] = ["all", ...VOICE_GENDERS];
export const VOICE_GENDER_FILTER_LABELS: Record<VoiceGenderFilter, string> = { all: "All", ...VOICE_GENDER_LABELS };

export interface GeminiVoice {
  id: GeminiVoiceId;
  gender: VoiceGender;
  /** Google's one word for how the voice sounds: "Bright", "Firm", "Warm"… */
  style: string;
}

/** Every voice with its gender and style, A to Z: the order the picker lists them in. */
export const GEMINI_VOICES: readonly GeminiVoice[] = [...(shared.voices as GeminiVoice[])].sort((a, b) =>
  a.id.localeCompare(b.id),
);

/** The voice a stored voice id names, case ignored; undefined for an id that is not one of the 30. */
export const geminiVoiceOf = (voiceId: string): GeminiVoice | undefined =>
  GEMINI_VOICES.find((voice) => voice.id.toLowerCase() === voiceId.toLowerCase());

/** A stored voice id as the dashboard names it: the voice's own name, case ignored; any other id as stored. */
export const voiceDisplayName = (voiceId: string): string => geminiVoiceOf(voiceId)?.id ?? voiceId;

/** The voices a search and the gender filter leave, A to Z. The search matches a name or a style, case ignored. */
export function findVoices(search: string, gender: VoiceGenderFilter): GeminiVoice[] {
  const term = search.trim().toLowerCase();
  return GEMINI_VOICES.filter(
    (voice) =>
      (gender === "all" || voice.gender === gender) &&
      (voice.id.toLowerCase().includes(term) || voice.style.toLowerCase().includes(term)),
  );
}

/** Root-relative path of a voice's sample clip (public/recordings). */
export const voiceSamplePath = (voiceId: string): string => `/recordings/${voiceId.toLowerCase()}-sample.wav`;
